import { useMemo } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { sql, PostgreSQL } from '@codemirror/lang-sql';
import { EditorView } from '@codemirror/view';
import clsx from 'clsx';

interface SqlEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  readOnly?: boolean;
  className?: string;
}

export const SqlEditor = ({
  value,
  onChange,
  placeholder,
  minHeight = '120px',
  maxHeight = '300px',
  readOnly = false,
  className,
}: SqlEditorProps) => {
  const extensions = useMemo(
    () => [
      sql({ dialect: PostgreSQL }),
      EditorView.lineWrapping,
      EditorView.theme({
        '&': {
          fontSize: '13px',
        },
        '&.cm-focused': {
          outline: 'none',
        },
        '.cm-scroller': {
          minHeight,
          maxHeight,
          overflow: 'auto',
        },
        '.cm-gutters': {
          backgroundColor: '#f9fafb',
          borderRight: '1px solid #e5e7eb',
          color: '#9ca3af',
        },
      }),
    ],
    [minHeight, maxHeight]
  );

  return (
    <div
      className={clsx(
        'rounded-md border border-gray-300 overflow-hidden',
        'focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500',
        readOnly && 'bg-gray-50',
        className
      )}
    >
      <CodeMirror
        value={value}
        onChange={onChange}
        extensions={extensions}
        placeholder={placeholder}
        readOnly={readOnly}
        basicSetup={{
          lineNumbers: true,
          bracketMatching: true,
          foldGutter: false,
          highlightActiveLine: true,
          autocompletion: false,
        }}
      />
    </div>
  );
};
